import { groupBy } from 'lodash';
import React from 'react';

import { Text } from '@fhs-legacy/universal-components';
import {
  ICombo,
  IComboSlot,
  IComboSlotOption,
  IComboSlotSelection,
  IComboSlotSelections,
  IItem,
  IItemOptionModifierWithQuantity,
  IModifierSelection,
  IPicker,
  IPickerPromotion,
} from '@rbi-ctg/menu';
import { ProductProps } from 'components/product/types';
import {
  computeModifierSelectionLabel,
  getModifierType,
} from 'components/product-detail/modifier/utils';
import { getModifiersDefaultOptions } from 'components/product-detail/product-customization/utils';
import { IProductListItemProps } from 'components/product-list-item/types';
import { MenuObjectTypes } from 'enums/menu';
import { ISanityImage } from 'generated/rbi-graphql';
import { computeProductCalories } from 'hooks/menu/utils';
import {
  IModifierSelectionActionWithData,
  IUserSelection,
  UserSelectionModifierSlotMap,
} from 'state/product-wizard/types';
import { isCombo, isItem } from 'utils/menu';

import { GetProductPropsSelectionsFn } from './types';

function getModifierImageBySelectionId({
  itemSelectionId,
  modifierSelections,
}: {
  itemSelectionId?: string;
  modifierSelections?: UserSelectionModifierSlotMap;
}) {
  if (!itemSelectionId || !modifierSelections) {
    return;
  }

  const [optionKey, modifierKey] = itemSelectionId.split(/[/-](.*)/);
  if (!optionKey || !modifierKey) {
    return;
  }
  // Using type assertion cause IItemOptionModifierWithQuantity is not typed correctly.
  const selectedModifier = modifierSelections?.[optionKey]?.[modifierKey]?.modifier as
    | (IItemOptionModifierWithQuantity & {
        image: ISanityImage;
      })
    | undefined;

  return selectedModifier?.image;
}

export const transformItemToProductListItemProps = (
  item: IItem | IPicker,
  extras?: {
    productNote?: string;
    isExpandable?: boolean;
    selections?: ProductProps['selections'];
    price?: number;
  }
): IProductListItemProps => ({
  isClickable: extras?.isExpandable,
  productProps: {
    name: item.name.locale,
    calories: String(computeProductCalories(item as IItem)),
    image: item.image ? JSON.stringify(item.image) : undefined,
    isProductNameBold: true,
    note: extras?.productNote,
    selections: extras?.selections,
    price: extras?.price,
  },
});

// TODO - GST-3340 Sauce Selection Customization: Remove this feature after content team has implemented detailed customization on their side.
export const transformItemAndVirtualItemToProductListItemProps = (
  item: IItem | IPicker,
  extras: {
    productNote?: string;
    isExpandable?: boolean;
    selections: ProductProps['selections'];
    modifierSelections: UserSelectionModifierSlotMap;
    price?: number;
  }
): IProductListItemProps => {
  const productListItemProps = transformItemToProductListItemProps(item, extras);

  let calories: ProductProps['calories'] = productListItemProps.productProps.calories;
  let image = productListItemProps.productProps.image;
  if (item._type === MenuObjectTypes.ITEM && item.isVirtualItem) {
    // Virtual items don't have calories.
    calories = undefined;

    // Virtual items image value should be equal to the default or first selected item modifier image prop.
    const defaultSelections = Object.keys(getModifiersDefaultOptions(item));
    const { modifierSelections, selections } = extras;
    const itemSelectionId = selections?.length ? selections[0].id : defaultSelections[0];
    const modifierImage = getModifierImageBySelectionId({ itemSelectionId, modifierSelections });
    image = modifierImage ? JSON.stringify(modifierImage) : image;
  }

  return {
    ...productListItemProps,
    productProps: {
      ...productListItemProps.productProps,
      calories,
      image,
    },
  };
};

export const getProductPropsSelections: GetProductPropsSelectionsFn = ({
  formatCalories,
  formatCurrencyForLocale,
  formatMessage,
  modifierSelections = {},
  priceForItemOptionModifier,
}) => {
  const productPropsSelections: NonNullable<ProductProps['selections']> = [];
  Object.entries(modifierSelections).forEach(([, selectionsMap]) => {
    Object.values(selectionsMap).forEach(({ option, modifier, isSanityDefaultSelection, item }) => {
      if (modifier.default) {
        return;
      }
      if (isSanityDefaultSelection || modifier.quantity <= 0) {
        return;
      }

      const label = computeModifierSelectionLabel({
        modifierType: getModifierType(option),
        modifierOption: modifier,
        modifierName: option.name?.locale,
        formatMessage,
      });

      const modifierCalories = modifier.nutrition?.calories || 0;
      const caloriesLabel = `${formatCalories(Math.abs(modifierCalories))}`;

      const modifierPrice = priceForItemOptionModifier({
        item,
        itemOption: option,
        modifier,
      });
      const upcharge = modifierPrice > 0 ? `+${formatCurrencyForLocale(modifierPrice)}` : '';

      const element = (
        <Text variant="copyTwo" fontSize="xs">
          {label} {'  '} {caloriesLabel} {'  '}
          <Text variant="copyTwo" fontWeight="bold">
            {upcharge}
          </Text>
        </Text>
      );

      productPropsSelections.push({
        id: `${option._key}-${modifier._key}`,
        element,
      });
    });
  });
  return productPropsSelections;
};

export const getModifiersActions = (
  modifierSlotMap: UserSelectionModifierSlotMap
): IModifierSelectionActionWithData[] => {
  return Object.values(modifierSlotMap ?? {})
    .map(modifierActionsMap => Object.values(modifierActionsMap))
    .flat();
};

export const getComboSlotOptionFromItem = (
  comboSlot?: IComboSlot | null,
  selectedItem?: IItem | null
) => {
  return comboSlot?.options.find(opt => opt.option._id === selectedItem?._id);
};

/*
 * NOTE: The following transform utils are needed to covert to IComboSlotSelections + IModifierSelection type
 * These types are used in other helper methods such as - pricing, cart, nutrition, etc.
 * New wizard has a UserSelections type that is slightly different due to new designs
 * Action item - clean up/resolve the two different types so both can be supported outside wizard
 */

export const transformUserSelectionsComboToComboSlotSelections = (
  selections: IUserSelection['comboSlot'] | undefined,
  selectedProduct?: ICombo
): IComboSlotSelections => {
  if (!selections) {
    return {};
  }
  const legacySelections: IComboSlotSelections = {};
  const selectionsAsArrayWithoutSelectedItem = Array.from(selections.values()).filter(
    sel => sel.comboSlot && !sel.selectedItem
  );
  const selectionsAsArrayWithSelectedItem = Array.from(selections.values()).filter(
    sel => sel.comboSlot && sel.selectedItem
  );
  const selectionsGroupedByComboSlot = groupBy(
    selectionsAsArrayWithSelectedItem,
    sel => sel.comboSlot?._id
  );
  Object.entries(selectionsGroupedByComboSlot).forEach(([comboSlotId, value]) => {
    const itemsGroupedByItemId = groupBy(value, val => val.selectedItem?._id);
    const comboSlotOptionsSelections: IComboSlotSelection[] = Object.values(
      itemsGroupedByItemId
    ).map(values => ({
      option: getComboSlotOptionFromItem(
        values[0]?.comboSlot,
        values[0]?.selectedItem
      ) as IComboSlotOption,
      quantity: values.length,
    }));
    legacySelections[comboSlotId] = {
      data: value[0].comboSlot as IComboSlot,
      selections: comboSlotOptionsSelections,
    };
  });

  // When we have locked/collapsed combo slots
  if (selectionsAsArrayWithoutSelectedItem.length) {
    selectionsAsArrayWithoutSelectedItem.forEach(({ comboSlot }) => {
      const comboSlotId = comboSlot?._id;
      if (comboSlotId) {
        legacySelections[comboSlotId] = {
          data: comboSlot as IComboSlot,
          selections: (comboSlot?.options || []).map(opt => ({
            option: opt,
            quantity: opt.defaultAmount,
          })),
        };
      }
    });
  }

  // Include hidden slots that are omitted from IUserSelections state
  const hiddenSlots = (selectedProduct?.options || []).reduce((acc, slot) => {
    if (slot.uiPattern === 'hidden') {
      acc[slot._id] = {
        data: slot,
        selections: [
          {
            option: slot?.options[0],
            quantity: slot?.options.length,
          },
        ],
      };
    }
    return acc;
  }, {} as IComboSlotSelections);

  return {
    ...legacySelections,
    ...hiddenSlots,
  };
};

export const transformUserSelectionsModifierToModifierSelection = (
  selections: IUserSelection
): IModifierSelection[] =>
  Object.entries(selections.modifiers).flatMap(([modifierSlotKey, modifierSlotMap]) =>
    Object.values(modifierSlotMap).flatMap(modifierActionsMap =>
      Object.values(modifierActionsMap)
        .filter(({ modifier }) => modifier.quantity > 0)
        .map(({ itemId, modifier, option }) => ({
          _key: option._key,
          _type: option._type,
          comboSlotId: selections.comboSlot?.get(modifierSlotKey)?.comboSlot?._id,
          componentStyle: option.componentStyle,
          allowMultipleSelections: option.allowMultipleSelections,
          itemId,
          fifoPositions: [], // not being used in new wizard (first in first out concept)
          modifier,
          name: option.name,
          upsellModifier: option.upsellModifier,
          injectDefaultSelection: option.injectDefaultSelection,
        }))
    )
  );

/* END NOTE */

export const createPickerPromotionMap = (
  options: IPicker['options']
): { [key: string]: IPickerPromotion } =>
  (options || []).reduce((acc, { pickerItemMappings, option }) => {
    if ((isItem(option) || isCombo(option)) && option.promotion && pickerItemMappings?.length) {
      const { promotion } = option;
      // using the latest mapping item to reach out the item/combo itself when the user finalize the selection
      const { pickerAspectValueIdentifier, pickerAspect: itemPickerAspect } =
        pickerItemMappings.slice(-1)[0];
      acc[`${itemPickerAspect._id}-${pickerAspectValueIdentifier}`] = promotion;
    }

    return acc;
  }, {});
